import { Peer } from "../Peer";
import {
  DataChannelEvent,
  GotAnswerEvent,
  GotOfferEvent,
  PeerEvents,
  SendAnswerEvent,
  SendCandidateEvent,
  SendOfferEvent,
  TrackEvent
} from "../dto";

export class WebRtcPeerService extends EventTarget {
  private pc: RTCPeerConnection;
  private makingOffer = false;
  private ignoreOffer = false;
  private isSettingRemoteAnswerPending = false;
  public constructor(private peer: Peer) {
    super();
    this.pc = new RTCPeerConnection(peer.config);
    this.pc.ontrack = (e) => {
      this.dispatchEvent(
        new CustomEvent<TrackEvent>(PeerEvents.TRACK_EVENT, {
          detail: {
            peer: this.peer,
            receiver: e.receiver,
            streams: e.streams,
            transceiver: e.transceiver,
            track: e.track
          }
        })
      );
    };
    this.pc.ondatachannel = (e) => {
      this.dispatchEvent(
        new CustomEvent<DataChannelEvent>(PeerEvents.ADD_DATA_CHANNEL, {
          detail: {
            peer: this.peer,
            channel: e.channel
          }
        })
      );
    };
    this.pc.onicecandidate = ({ candidate }) => {
      if (candidate == null) {
        return;
      }
      console.log("[ICE CANDIDATE]", this.pc.signalingState);
      this.dispatchEvent(
        new CustomEvent<SendCandidateEvent>(PeerEvents.SEND_CANDIDATE, {
          detail: {
            candidate,
            to: this.peer.userRemoteId
          }
        })
      );
    };

    this.pc.onnegotiationneeded = this.onNegotiation.bind(this);

    this.pc.onconnectionstatechange = () => {
      console.log("[CONNECTION STATE CHANGED]", this.pc.connectionState);
    };

    this.pc.onsignalingstatechange = () => {
      console.log("[SIGNALING STATE CHANGED]", this.pc.signalingState);
    };

    this.pc.onicegatheringstatechange = () => {
      console.log("[ICE GATHERING STATE CHANGED]", this.pc.iceGatheringState);
    };

    this.pc.oniceconnectionstatechange = () => {
      console.log("[ICE CONNECTION STATE CHANGED]", this.pc.iceConnectionState);
      if (this.pc.iceConnectionState === "disconnected") {
        this.dispatchEvent(new Event(PeerEvents.DISCONNECT));
      }
    };
  }

  private dispatchOffer(from?: number) {
    console.log("dispatchOffer", from);
    this.dispatchEvent(
      new CustomEvent<SendOfferEvent>(PeerEvents.SEND_OFFER, {
        detail: {
          description: this.pc.localDescription,
          from,
          to: this.peer.userRemoteId
        }
      })
    );
  }

  private dispatchAnswer(from: number) {
    console.log("dispatchAnswer", from);
    this.dispatchEvent(
      new CustomEvent<SendAnswerEvent>(PeerEvents.SEND_ANSWER, {
        detail: {
          description: this.pc.localDescription,
          from,
          to: this.peer.userRemoteId
        }
      })
    );
  }

  private dispatchGotOffer(from: number) {
    this.dispatchEvent(
      new CustomEvent<GotOfferEvent>(PeerEvents.ON_GOT_OFFER, {
        detail: { from, to: this.peer.userRemoteId }
      })
    );
  }

  private dispathGotAnswer(from: number) {
    this.dispatchEvent(
      new CustomEvent<GotAnswerEvent>(PeerEvents.ON_GOT_ANSWER, {
        detail: { from, to: this.peer.userRemoteId }
      })
    );
  }

  public async onNegotiation() {
    console.log(
      "onNegotiation",
      this.pc.signalingState,
      this.pc.iceGatheringState
    );
    try {
      this.makingOffer = true;
      await this.pc.setLocalDescription();
      this.dispatchOffer();
    } catch (e) {
      console.warn(e);
    } finally {
      this.makingOffer = false;
    }
  }

  public close() {
    this.pc.close();
  }

  public getTransceivers(): RTCRtpTransceiver[] {
    return this.pc.getTransceivers();
  }

  public addTrack(track: MediaStreamTrack): RTCRtpSender {
    return this.pc.addTrack(track);
  }

  public async replaceTrack(stream: MediaStream): Promise<void> {
    const transceivers = this.pc.getTransceivers();
    const tracks = stream.getTracks();
    for (const track of tracks) {
      const transceiver = transceivers.find(
        (t) => t.receiver.track.kind === track.kind
      );
      if (transceiver) {
        await transceiver.sender.replaceTrack(track);
        if (
          transceiver.direction === "recvonly" ||
          transceiver.direction === "inactive"
        ) {
          transceiver.direction = "sendrecv";
        }
      } else {
        this.pc.addTrack(track);
      }
    }
  }

  public removeTrack(): void {
    const senders = this.pc.getSenders();
    for (const sender of senders) {
      this.pc.removeTrack(sender);
    }
  }

  public addTransceiver(
    trackOrKind: string | MediaStreamTrack,
    init: RTCRtpTransceiverInit
  ): RTCRtpTransceiver {
    return this.pc.addTransceiver(trackOrKind, init);
  }

  public createDataChannel(label: string): RTCDataChannel | null {
    return this.pc.createDataChannel(label);
  }

  public async getStats(): Promise<RTCStatsReport> {
    return await this.pc.getStats();
  }

  public async handleDescription(
    description: RTCSessionDescription,
    from: number
  ) {
    console.log("Peer handleDescription polite:", this.peer.polite);
    const isStable =
      this.pc.signalingState == "stable" ||
      (this.pc.signalingState == "have-local-offer" &&
        this.isSettingRemoteAnswerPending);
    this.ignoreOffer =
      description.type == "offer" &&
      !this.peer.polite &&
      (this.makingOffer || !isStable);
    if (this.ignoreOffer) {
      console.warn(
        `${this.pc} ignore offer because of offer collision ${this.pc.signalingState}`
      );
      return;
    }
    this.isSettingRemoteAnswerPending = description.type === "answer";
    await this.pc.setRemoteDescription(description);
    this.isSettingRemoteAnswerPending = false;
    if (description.type === "offer") {
      console.log("Peer handleDescription offer", description);
      await this.pc.setLocalDescription();
      this.dispatchAnswer(from);
      this.dispatchGotOffer(from);
    } else {
      this.dispathGotAnswer(from);
      this.pc.dispatchEvent(new Event("negotiated"));
    }
  }

  public async handleCandidate(candidate: RTCIceCandidate) {
    try {
      await this.pc.addIceCandidate(candidate);
      console.log(
        "[ICE CANDIDATE ADDED] at signaling state: ",
        this.pc.signalingState
      );
    } catch {
      if (!this.ignoreOffer)
        console.warn(
          `${this.pc} this candidate can't accept current signaling state ${this.pc.signalingState}.`
        );
    }
  }
}
